import {
  ICommonSettings,
  IGeminiSetting,
  IImageLibraryDto,
  VERTEX_AI_LOCATION_SUGGESTIONS
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

// Suggestions for the Location picker, not a permitted-value set: the control is a combobox, so a
// location absent from this list can still be typed and saved. `value` and `label` are both the id —
// every Vertex AI id already names its geography, so an option group would add nothing to read and
// would risk a display label reaching the saved value.
const vertexLocationOptions: KuikaSelectboxOption[] = VERTEX_AI_LOCATION_SUGGESTIONS.map(
  (location: string): KuikaSelectboxOption => ({ value: location, label: location })
);

// Required, not a refinement: a combobox with no filterOption defaults to showing every option, so
// without this typing "eu" would still list all of them. `toLowerCase` is locale-independent, unlike
// its locale-aware variant, so the match behaves identically under a Turkish system locale.
const filterVertexLocationOption = (input: string, option: KuikaSelectboxOption | undefined): boolean =>
  String(option?.value ?? "")
    .toLowerCase()
    .includes(input.trim().toLowerCase());

export interface IConfigGeminiSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingGemini?: IGeminiSetting;
  allImage?: IImageLibraryDto[];
  serviceAccountJSONFileName: string;
}

export interface IConfigGeminiSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGeminiId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigGeminiSettings extends React.PureComponent<IConfigGeminiSettingsProps, IConfigGeminiSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigGeminiSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      allImage: [],
      serviceAccountJSONFileName: ""
    };
  }

  componentDidMount() {
    this.loadGeminiSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigGeminiSettingsProps) {
    if (prevProps.editingGeminiId !== this.props.editingGeminiId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadGeminiSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadGeminiSetting();
    }
  }

  loadGeminiSetting = () => {
    if (!this.props.editingGeminiId) return;

    const editingGemini = this.props.commonSettings.geminiSettings?.find(
      (x) => x.id === this.props.editingGeminiId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingGemini) return;
    this.setState({ editingGemini }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingGemini);
      this.setState({ serviceAccountJSONFileName: editingGemini.serviceAccountJson || "" });
    });
  };

  existingNameValidator = async (_rule, value) => {
    const geminisSettings = this.props.commonSettings?.geminiSettings;

    const isSettingAlreadyExist =
      geminisSettings?.some(
        (x) => x.name === value && x.id !== this.props.editingGeminiId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Gemini Settings Name is already exist.");
  };

  onFinish = (data: any) => {
    if (!this.state.serviceAccountJSONFileName) {
      KMainFunctions.displayWarningNotification("Please enter a Server Key or choose a Service Account Json file");
      return;
    }
    this.saveStatus.start();

    // An empty box means "no preference", which the backend resolves to the global Vertex AI
    // endpoint. Send the key only when a region is actually pinned, so clearing the box clears it.
    const pinnedLocation: string = (data.location ?? "").trim().toLowerCase();

    let obj: IGeminiSetting;
    if (this.props.editingGeminiId) {
      obj = {
        id: this.props.editingGeminiId,
        name: data.name,
        serviceAccountJson: this.state.serviceAccountJSONFileName,
        location: pinnedLocation || undefined
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "geminiSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        serviceAccountJson: this.state.serviceAccountJSONFileName,
        location: pinnedLocation || undefined
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "geminiSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteGemini = () => {
    if (!this.props.editingGeminiId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingGeminiId || "",
      configKey: "geminiSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Gemini setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Gemini Setting"
        secondHeader="Gemini Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingGeminiId ? this.deleteGemini : undefined}
            submitLabel={this.props.editingGeminiId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGeminiForm"
          />
        }
      >
        <Form
          id="configGeminiForm"
          name="geminiSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_geminisettingpop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_geminisettingpop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Gemini Setting Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Gemini Setting Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Service Account Json"
              required
              tooltipContentKey="appsettings_openaisettingpop_up_serviceaccountjson"
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".json"
                getImageLibrary={this.getImageLibrary}
                selectedId={
                  this.state.allImage?.find(
                    (x: IImageLibraryDto): boolean => x.fileName === this.state.serviceAccountJSONFileName
                  )?.id
                }
                fileName={this.state.serviceAccountJSONFileName}
                onSelect={(image: IImageLibraryDto): void =>
                  this.setState({ serviceAccountJSONFileName: image.fileName }, (): void => {
                    this.formRef.current?.validateFields().catch((): void => {});
                  })
                }
                onClear={(): void => this.setState({ serviceAccountJSONFileName: "" })}
              />
            </ConfigField>
            <ConfigField
              label="Location"
              tooltipContentKey="appsettings_geminisettingpop_up_location"
              // Empty is Vertex AI's global endpoint, which pools capacity across regions and
              // returns far fewer rate-limit errors. A region is for data-residency, not tuning.
              hint="Leave empty to use Vertex AI's global endpoint, which pools capacity across regions and returns far fewer rate-limit errors. Pick a region, or type any Vertex AI location id, only when the data must be processed there."
            >
              <Form.Item
                name="location"
                rules={[
                  {
                    pattern: /^[A-Za-z0-9-]+$/,
                    message: "Location must be a Vertex AI location id, e.g. europe-west4"
                  }
                ]}
              >
                {/* The ids are suggestions, not a permitted set — any Vertex AI location can be typed. */}
                <KuikaSelectbox
                  freeText
                  size="md"
                  placeholder="global"
                  options={vertexLocationOptions}
                  filterOption={filterVertexLocationOption}
                />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
